import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getMediaPath } from 'app/functions';
import { TextHot } from 'components/common/TextHot';
import CardBackgroundImage from 'assets/HotYoter/Vector 5.svg';
import Number0Icon from 'assets/HotYoter/number0.svg';
import Number1Icon from 'assets/HotYoter/number1.svg';
import Number2Icon from 'assets/HotYoter/number2.svg';
import NumberGoldIcon from 'assets/HotYoter/numbergold.svg';
import {
    canFitCardsInline,
    CARD_SVG,
    getCardContentLayout,
    getCardDimensions,
    getInlineCardWidth,
    getResponsiveCardWidth,
    getUniformCardHeightOverride,
    INLINE_CARD_GAP,
    LG_INLINE_CARD_GAP,
    MAX_CARD_WIDTH,
    MD_INLINE_CARD_GAP,
    MD_LG_SCROLL_MAX_CARD_WIDTH,
    MOBILE_MAX_CARD_WIDTH,
    SCROLL_PEEK_VISIBLE_CARDS_MD,
    SCROLL_PEEK_VISIBLE_CARDS_MD_LG,
    SCROLL_PEEK_VISIBLE_CARDS_MOBILE,
    SCROLL_PEEK_VISIBLE_CARDS_TABLET,
    TABLET_SCROLL_MAX_CARD_WIDTH,
    XL_INLINE_CARD_WIDTH,
    XL_MIN_INLINE_CARD_WIDTH,
} from './cardLayout';

const getScrollSizingOptions = ({ isTabletUp, isMdUp, isXlUp }) => {
    if (isMdUp && !isXlUp) {
        return {
            visibleCards: SCROLL_PEEK_VISIBLE_CARDS_MD_LG,
            gap: MD_INLINE_CARD_GAP,
            scrollMaxWidth: MD_LG_SCROLL_MAX_CARD_WIDTH,
        };
    }

    if (isXlUp) {
        return {
            visibleCards: SCROLL_PEEK_VISIBLE_CARDS_MD,
            gap: LG_INLINE_CARD_GAP,
            scrollMaxWidth: XL_INLINE_CARD_WIDTH,
        };
    }

    if (isTabletUp) {
        return {
            visibleCards: SCROLL_PEEK_VISIBLE_CARDS_TABLET,
            gap: INLINE_CARD_GAP,
            scrollMaxWidth: TABLET_SCROLL_MAX_CARD_WIDTH,
        };
    }

    return {
        visibleCards: SCROLL_PEEK_VISIBLE_CARDS_MOBILE,
        gap: INLINE_CARD_GAP,
        scrollMaxWidth: MOBILE_MAX_CARD_WIDTH,
    };
};

const NUMBER_BADGE_ICONS = {
    0: Number0Icon,
    1: Number1Icon,
    2: Number2Icon,
};

const TABLET_UP = '@media (min-width: 768px)';

const CarouselWrapper = styled('div')({
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
});

const CarouselRoot = styled('div', {
    shouldForwardProp: (prop) => prop !== 'allowVerticalOverflow',
})(({ theme, allowVerticalOverflow }) => ({
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: allowVerticalOverflow ? 'visible' : 'hidden',
    direction: 'rtl',
    display: 'flex',
    flexDirection: 'column',
    [TABLET_UP]: {
        flex: 1,
        minWidth: 0,
        maxWidth: '100%',
    },
}));

const CarouselScroll = styled('div', {
    shouldForwardProp: (prop) => prop !== 'fitsInline'
        && prop !== 'cardRowHeight'
        && prop !== 'hasHeightOverride'
        && prop !== 'isDesktopInline'
        && prop !== 'isScrollMode',
})(({ theme, fitsInline, cardRowHeight, hasHeightOverride, isDesktopInline, isScrollMode }) => ({
    display: 'flex',
    flexDirection: 'row',
    direction: isScrollMode ? 'rtl' : 'ltr',
    gap: theme.spacing(1),
    overflowX: 'auto',
    padding: `${theme.spacing(0.25)} 0 ${theme.spacing(0.5)}`,
    scrollSnapType: 'x mandatory',
    scrollSnapStop: 'normal',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    ...(cardRowHeight != null && {
        minHeight: cardRowHeight,
        ...(hasHeightOverride ? {} : { maxHeight: cardRowHeight }),
    }),
    ...(fitsInline && {
        overflow: 'visible',
        paddingRight: 0,
        paddingLeft: 0,
        paddingBottom: 0,
        justifyContent: isDesktopInline ? 'flex-end' : 'flex-start',
        width: '100%',
    }),
    [theme.breakpoints.up('sm')]: {
        gap: theme.spacing(1.25),
        paddingBottom: 0,
    },
    [theme.breakpoints.up('md')]: {
        gap: theme.spacing(1.5),
    },
    [theme.breakpoints.up('lg')]: {
        gap: theme.spacing(2),
    },
}));

const CarouselDots = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    direction: 'rtl',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.25),
    width: '100%',
}));

const CarouselDot = styled('button', {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
    width: 6,
    height: 6,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    flexShrink: 0,
    background: active ? '#000' : 'rgba(0, 0, 0, 0.25)',
    transition: 'background 0.2s ease, transform 0.2s ease',
    ...(active && {
        transform: 'scale(1.35)',
    }),
}));

const CardOuter = styled('div', {
    shouldForwardProp: (prop) => prop !== 'fitsInline' && prop !== 'layoutWidth',
})(({ theme, fitsInline, layoutWidth }) => ({
    flex: fitsInline ? '1 1 0' : '0 0 auto',
    minWidth: fitsInline ? 0 : undefined,
    // Cap growth to the measured layout width so very wide viewports don't
    // stretch cards beyond the coordinates used for text positioning.
    maxWidth: layoutWidth != null ? layoutWidth : (fitsInline ? 'none' : undefined),
    scrollSnapAlign: 'start',
    filter: `drop-shadow(0 ${theme.spacing(0.375)} ${theme.spacing(1.25)} rgba(46, 99, 238, 0.14))`,
    ...(fitsInline && {
        display: 'flex',
        justifyContent: 'center',
    }),
}));

const Card = styled('div')({
    position: 'relative',
    overflow: 'visible',
    direction: 'ltr',
});

const getCardSurfaceStyle = (layout) => (
    layout.isTallCard
        ? {
            backgroundColor: '#fff',
            borderBottomLeftRadius: `${Math.round((20 / CARD_SVG.width) * layout.width)}px`,
            borderBottomRightRadius: `${Math.round((20 / CARD_SVG.width) * layout.width)}px`,
        }
        : undefined
);

const CardBackground = styled('img')({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'block',
    pointerEvents: 'none',
    objectFit: 'fill',
});

const CardBadge = styled('img')({
    position: 'absolute',
    zIndex: 2,
    display: 'block',
    pointerEvents: 'none',
});

const CardTitleContent = styled('div')({
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1,
    overflow: 'hidden',
    alignItems: 'stretch',
    textAlign: 'center',
    direction: 'rtl',
    justifyContent: 'center',
});

const BODY_CONTENT_MARGIN_TOP = 10;

const CardBodyContent = styled('div')({
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1,
    overflow: 'hidden',
    alignItems: 'stretch',
    direction: 'rtl',
    textAlign: 'right',
    justifyContent: 'flex-start',
    marginTop: `${BODY_CONTENT_MARGIN_TOP}px`,
});

const CardBodyInner = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'stretch',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
});

const getCardTextSizes = (isMdUp) => ({
    title: 'm',
    bodyBold: isMdUp ? 'm' : 'l',
    bodySubtle: 'm',
});

const getTitleTextSx = () => ({
    m: 0,
    width: '100%',
    textAlign: 'center',
    direction: 'rtl',
    unicodeBidi: 'plaintext',
});

const getBodyTextSx = () => ({
    m: 0,
    width: '100%',
    textAlign: 'right',
    direction: 'rtl',
    unicodeBidi: 'plaintext',
    wordBreak: 'break-word',
});

const getClampStyle = (lines) => ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
});

const getBadgeCount = (index) => index;

const getCarouselItems = (sections, reverseForScroll) => {
    const items = sections.map((section, index) => ({
        section,
        index,
        key: `${section.title}-${index}`,
    }));

    return reverseForScroll ? [...items].reverse() : items;
};

const scrollCarouselToGoldStart = (element) => {
    if (!element) {
        return;
    }

    element.scrollLeft = 0;

    requestAnimationFrame(() => {
        element.scrollLeft = 0;
    });
};

const getDomIndexForLogicalIndex = (logicalIndex, slideCount, isScrollMode) => (
    isScrollMode ? slideCount - 1 - logicalIndex : logicalIndex
);

const getLogicalIndexFromDomIndex = (domIndex, slideCount, isScrollMode) => (
    isScrollMode ? slideCount - 1 - domIndex : domIndex
);

const scrollToLogicalSlide = (element, logicalIndex, slideCount, isScrollMode) => {
    if (!element || slideCount <= 1) {
        return;
    }

    const domIndex = getDomIndexForLogicalIndex(logicalIndex, slideCount, isScrollMode);
    const target = element.children[domIndex];

    if (target) {
        target.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
        });
    }
};

const findMostVisibleDomIndex = (scrollEl) => {
    const rootRect = scrollEl.getBoundingClientRect();
    let bestDom = 0;
    let bestOverlap = 0;

    Array.from(scrollEl.children).forEach((child, domIndex) => {
        const rect = child.getBoundingClientRect();
        const overlap = Math.max(
            0,
            Math.min(rect.right, rootRect.right) - Math.max(rect.left, rootRect.left),
        );

        if (overlap > bestOverlap) {
            bestOverlap = overlap;
            bestDom = domIndex;
        }
    });

    return bestDom;
};

const getActiveSlideIndexFromScroll = (scrollEl, slideCount, isScrollMode) => {
    const domIndex = findMostVisibleDomIndex(scrollEl);

    return getLogicalIndexFromDomIndex(domIndex, slideCount, isScrollMode);
};

const getAbsoluteBoxStyle = ({ top, bottom, left, right, gap }) => ({
    top: `${top}px`,
    bottom: `${bottom}px`,
    left: `${left}px`,
    right: `${right}px`,
    ...(gap != null ? { gap: `${gap}px` } : {}),
});

const measureNaturalSubtitleHeight = (subtitleWrap, textWidth) => {
    const subtitleEl = subtitleWrap?.firstElementChild;

    if (!subtitleEl) {
        return 0;
    }

    const previousDisplay = subtitleEl.style.display;
    const previousLineClamp = subtitleEl.style.webkitLineClamp;
    const previousOverflow = subtitleEl.style.overflow;
    const previousWidth = subtitleEl.style.width;

    subtitleEl.style.display = 'block';
    subtitleEl.style.webkitLineClamp = 'unset';
    subtitleEl.style.overflow = 'visible';
    if (textWidth > 0) {
        subtitleEl.style.width = `${textWidth}px`;
    }

    const height = subtitleEl.scrollHeight;

    subtitleEl.style.display = previousDisplay;
    subtitleEl.style.webkitLineClamp = previousLineClamp;
    subtitleEl.style.overflow = previousOverflow;
    subtitleEl.style.width = previousWidth;

    return height;
};

const measureNaturalBodyContentHeight = ({
    subtitleBoldRef,
    subtitleWrapRef,
    bodyGap,
    section,
    textWidth,
}) => {
    let total = 0;

    if (section.subtitleBold && subtitleBoldRef.current) {
        total += subtitleBoldRef.current.offsetHeight;
    }

    if (section.subtitle && subtitleWrapRef.current) {
        if (total > 0) {
            total += bodyGap;
        }

        total += measureNaturalSubtitleHeight(subtitleWrapRef.current, textWidth);
    }

    return total;
};

const getSubtitleLineHeight = (subtitleEl) => {
    const computed = window.getComputedStyle(subtitleEl);
    let lineHeight = parseFloat(computed.lineHeight);

    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
        lineHeight = parseFloat(computed.fontSize) * 1.2;
    }

    return Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : null;
};

const getAvailableSubtitleHeight = ({
    inner,
    subtitleBoldRef,
    bodyGap,
}) => {
    let available = inner.clientHeight;

    if (subtitleBoldRef.current) {
        available -= subtitleBoldRef.current.offsetHeight + bodyGap;
    }

    return Math.max(available, 0);
};

const HotYoterHowToGetCard = ({
    section,
    index,
    total,
    cardWidth,
    cardMaxWidth = MAX_CARD_WIDTH,
    cardHeightOverride = null,
    fitsInline = false,
    isMdUp = false,
    hasResolvedContentHeight = false,
    onBodyContentHeightReport,
    testID,
}) => {
    const layout = getCardContentLayout(cardWidth, cardMaxWidth, cardHeightOverride);
    const textSizes = getCardTextSizes(isMdUp);
    const badgeCount = getBadgeCount(index);
    const isGoldBadge = section?.badgeType === 'gold'
        || (index === total - 1 && total >= 4);

    const bodyInnerRef = useRef(null);
    const subtitleBoldRef = useRef(null);
    const subtitleWrapRef = useRef(null);
    const [preMeasureClamp, setPreMeasureClamp] = useState(1);
    const [overflowFallback, setOverflowFallback] = useState('none');

    useLayoutEffect(() => {
        const inner = bodyInnerRef.current;
        const subtitleWrap = subtitleWrapRef.current;

        if (!hasResolvedContentHeight) {
            setOverflowFallback('none');

            if (inner && subtitleWrap && section.subtitle) {
                const available = getAvailableSubtitleHeight({
                    inner,
                    subtitleBoldRef,
                    bodyGap: layout.bodyGap,
                });
                const subtitleEl = subtitleWrap.firstElementChild || subtitleWrap;
                const lineHeight = getSubtitleLineHeight(subtitleEl);

                if (lineHeight) {
                    const lines = Math.max(1, Math.floor(available / lineHeight));
                    setPreMeasureClamp((current) => (current === lines ? current : lines));
                }
            }

            const naturalHeight = measureNaturalBodyContentHeight({
                subtitleBoldRef,
                subtitleWrapRef,
                bodyGap: layout.bodyGap,
                section,
                textWidth: layout.bodyTextMaxWidth,
            });

            onBodyContentHeightReport?.(index, naturalHeight);
            return undefined;
        }

        if (!inner) {
            return undefined;
        }

        const applyOverflowFallback = () => {
            if (inner.scrollHeight <= inner.clientHeight + 1) {
                setOverflowFallback((current) => (current === 'none' ? current : 'none'));
                return;
            }

            if (subtitleWrap && section.subtitle) {
                const available = getAvailableSubtitleHeight({
                    inner,
                    subtitleBoldRef,
                    bodyGap: layout.bodyGap,
                });
                const subtitleEl = subtitleWrap.firstElementChild || subtitleWrap;
                const lineHeight = getSubtitleLineHeight(subtitleEl);

                if (lineHeight && available > 0) {
                    const lines = Math.max(1, Math.floor(available / lineHeight));
                    setPreMeasureClamp((current) => (current === lines ? current : lines));
                    setOverflowFallback((current) => (current === 'clamp' ? current : 'clamp'));
                    return;
                }
            }

            setOverflowFallback((current) => (current === 'scroll' ? current : 'scroll'));
        };

        applyOverflowFallback();

        const observer = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(applyOverflowFallback)
            : null;

        if (observer) {
            observer.observe(inner);
        }

        return () => {
            observer?.disconnect();
        };
    }, [
        section.subtitle,
        section.subtitleBold,
        layout.bodyGap,
        layout.bodyTextMaxWidth,
        layout.width,
        layout.height,
        isMdUp,
        hasResolvedContentHeight,
        cardHeightOverride,
        onBodyContentHeightReport,
        index,
    ]);

    useLayoutEffect(() => {
        if (!hasResolvedContentHeight || !bodyInnerRef.current) {
            return undefined;
        }

        const inner = bodyInnerRef.current;

        if (inner.scrollHeight > inner.clientHeight + 1) {
            onBodyContentHeightReport?.(index, inner.scrollHeight, 'refine');
        }

        return undefined;
    }, [
        hasResolvedContentHeight,
        cardHeightOverride,
        layout.width,
        layout.height,
        section.subtitle,
        section.subtitleBold,
        onBodyContentHeightReport,
        index,
    ]);

    const subtitleStyle = !hasResolvedContentHeight || overflowFallback === 'clamp'
        ? getClampStyle(preMeasureClamp)
        : undefined;

    const bodyInnerOverflow = overflowFallback === 'scroll' ? 'auto' : 'hidden';

    return (
        <CardOuter
            className="hot-yoter-how-to-get-carousel__card-outer"
            fitsInline={fitsInline}
            layoutWidth={layout.width}
            testId={`${testID}-card-${index}`}
            style={fitsInline ? { width: '100%' } : { width: layout.width }}
        >
            <Card
                className="hot-yoter-how-to-get-carousel__card"
                style={{
                    width: '100%',
                    maxWidth: layout.width,
                    height: layout.height,
                    ...getCardSurfaceStyle(layout),
                }}
            >
                <CardBackground
                    className="hot-yoter-how-to-get-carousel__card-background"
                    src={getMediaPath(CardBackgroundImage)}
                    alt=""
                    style={layout.isTallCard ? { height: layout.referenceHeight } : undefined}
                />

                {isGoldBadge ? (
                    <CardBadge
                        className="hot-yoter-how-to-get-carousel__badge"
                        src={getMediaPath(NumberGoldIcon)}
                        alt=""
                        testId={`${testID}-badge-${index}`}
                        style={{
                            left: layout.badge.goldLeft,
                            top: layout.badge.goldTop,
                            width: layout.badgeCircleSize,
                            height: layout.badgeCircleSize,
                        }}
                    />
                ) : (
                    <CardBadge
                        className="hot-yoter-how-to-get-carousel__badge"
                        src={getMediaPath(NUMBER_BADGE_ICONS[badgeCount] ?? Number0Icon)}
                        alt=""
                        testId={`${testID}-badge-${index}`}
                        style={{
                            left: layout.badge.left,
                            top: layout.badge.top,
                            width: layout.badge.imgWidth,
                            height: layout.badge.imgHeight,
                        }}
                    />
                )}

                <CardTitleContent
                    className="hot-yoter-how-to-get-carousel__title-content"
                    dir="rtl"
                    testId={`${testID}-card-title-content-${index}`}
                    style={getAbsoluteBoxStyle(layout.title)}
                >
                    <TextHot
                        size={textSizes.title}
                        bold="bold"
                        color="primary"
                        component="p"
                        sx={getTitleTextSx()}
                        testID={`${testID}-card-title-${index}`}
                    >
                        {section.title}
                    </TextHot>
                </CardTitleContent>

                <CardBodyContent
                    className="hot-yoter-how-to-get-carousel__body-content"
                    dir="rtl"
                    testId={`${testID}-card-body-content-${index}`}
                    style={getAbsoluteBoxStyle(layout.body)}
                >
                    <CardBodyInner
                        ref={bodyInnerRef}
                        className="hot-yoter-how-to-get-carousel__body-inner"
                        style={{
                            gap: `${layout.bodyGap}px`,
                            overflowY: bodyInnerOverflow,
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        {section.subtitleBold ? (
                            <div
                                ref={subtitleBoldRef}
                                className="hot-yoter-how-to-get-carousel__body-bold-wrap"
                                style={{ flexShrink: 0, width: '100%' }}
                            >
                                <TextHot
                                    size={textSizes.bodyBold}
                                    bold="bold"
                                    color="primary"
                                    component="p"
                                    className="hot-yoter-how-to-get-carousel__body-text hot-yoter-how-to-get-carousel__body-text_bold"
                                    sx={getBodyTextSx()}
                                    testID={`${testID}-card-bold-${index}`}
                                >
                                    {section.subtitleBold}
                                </TextHot>
                            </div>
                        ) : null}
                        {section.subtitle ? (
                            <div
                                ref={subtitleWrapRef}
                                className="hot-yoter-how-to-get-carousel__body-subtitle-wrap"
                                style={{
                                    flex: '1 1 auto',
                                    minHeight: 0,
                                    width: '100%',
                                    overflow: overflowFallback === 'scroll' ? 'visible' : 'hidden',
                                }}
                            >
                                <TextHot
                                    size={textSizes.bodySubtle}
                                    color="subtle"
                                    component="p"
                                    className="hot-yoter-how-to-get-carousel__body-text hot-yoter-how-to-get-carousel__body-text_subtle"
                                    sx={getBodyTextSx()}
                                    style={subtitleStyle}
                                    testID={`${testID}-card-subtitle-${index}`}
                                >
                                    {section.subtitle}
                                </TextHot>
                            </div>
                        ) : null}
                    </CardBodyInner>
                </CardBodyContent>
            </Card>
        </CardOuter>
    );
};

export const HotYoterHowToGetCarousel = ({
    sections = [],
    onCardRowHeightChange,
    testID = 'hot-yoter-how-to-get-carousel',
}) => {
    const theme = useTheme();
    const wrapperRef = useRef(null);
    const scrollRef = useRef(null);
    const measureFrameRef = useRef(null);
    const [cardWidth, setCardWidth] = useState(180);
    const [fitsInline, setFitsInline] = useState(false);
    const [activeSlideIndex, setActiveSlideIndex] = useState(() => (
        sections.length > 0 ? sections.length - 1 : 0
    ));
    const deviceState = useSelector((store) => store.state.deviceState);
    const isMobile = deviceState === 'mobile';
    const isTabletUp = useMediaQuery('(min-width:768px)');
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
    const isScrollMode = !fitsInline;
    const inlineMaxWidth = isXlUp ? XL_INLINE_CARD_WIDTH : MAX_CARD_WIDTH;
    const scrollSizing = getScrollSizingOptions({ isTabletUp, isMdUp, isXlUp });
    const cardMaxWidth = isScrollMode ? scrollSizing.scrollMaxWidth : inlineMaxWidth;
    const bodyContentHeightsRef = useRef({});
    const contentHeightResolvedRef = useRef(false);
    const contentHeightRefineRef = useRef(false);
    const [cardHeightOverride, setCardHeightOverride] = useState(null);
    const [hasResolvedContentHeight, setHasResolvedContentHeight] = useState(false);
    const cardRowHeight = getCardDimensions(
        cardWidth,
        cardMaxWidth,
        cardHeightOverride,
    ).height;

    const handleBodyContentHeightReport = useCallback((index, height, mode = 'initial') => {
        if (mode === 'refine') {
            if (contentHeightRefineRef.current || !Number.isFinite(height) || height <= 0) {
                return;
            }

            bodyContentHeightsRef.current[index] = Math.max(
                bodyContentHeightsRef.current[index] || 0,
                height,
            );

            const refinedOverride = getUniformCardHeightOverride(
                sections.map((_, sectionIndex) => bodyContentHeightsRef.current[sectionIndex]),
                cardWidth,
                cardMaxWidth,
                { bodyTopMargin: BODY_CONTENT_MARGIN_TOP },
            );

            if (refinedOverride == null) {
                return;
            }

            contentHeightRefineRef.current = true;

            setCardHeightOverride((currentOverride) => (
                currentOverride == null || refinedOverride > currentOverride
                    ? refinedOverride
                    : currentOverride
            ));
            return;
        }

        if (contentHeightResolvedRef.current) {
            return;
        }

        bodyContentHeightsRef.current[index] = height;

        const reportedHeights = sections.map((_, sectionIndex) => (
            bodyContentHeightsRef.current[sectionIndex]
        ));

        if (reportedHeights.some((reportedHeight) => reportedHeight == null)) {
            return;
        }

        contentHeightResolvedRef.current = true;

        setCardHeightOverride(getUniformCardHeightOverride(
            reportedHeights,
            cardWidth,
            cardMaxWidth,
            { bodyTopMargin: BODY_CONTENT_MARGIN_TOP },
        ));
        setHasResolvedContentHeight(true);
    }, [sections, cardWidth, cardMaxWidth]);
    const isDesktopInline = fitsInline && isTabletUp && !isMobile;
    const showScrollDots = isScrollMode && sections.length > 1;
    const carouselItems = useMemo(
        () => getCarouselItems(sections, isScrollMode),
        [sections, isScrollMode],
    );
    const dotItems = useMemo(
        () => [...sections].reverse().map((section, reverseIndex) => ({
            section,
            index: sections.length - 1 - reverseIndex,
        })),
        [sections],
    );

    const handleDotClick = useCallback((index) => {
        scrollToLogicalSlide(scrollRef.current, index, sections.length, isScrollMode);
        setActiveSlideIndex(index);
    }, [sections.length, isScrollMode]);

    const syncActiveSlideFromScroll = useCallback(() => {
        const element = scrollRef.current;

        if (!element || !isScrollMode) {
            return;
        }

        setActiveSlideIndex(getActiveSlideIndexFromScroll(
            element,
            sections.length,
            isScrollMode,
        ));
    }, [isScrollMode, sections.length]);

    useLayoutEffect(() => {
        if (!onCardRowHeightChange) {
            return undefined;
        }

        onCardRowHeightChange(isTabletUp && !isMobile ? cardRowHeight : null);

        return undefined;
    }, [cardRowHeight, isTabletUp, isMobile, onCardRowHeightChange]);

    useLayoutEffect(() => {
        const measure = () => {
            if (!wrapperRef.current) {
                return;
            }

            const containerWidth = wrapperRef.current.clientWidth;
            const inlineGap = LG_INLINE_CARD_GAP;
            const scrollOptions = getScrollSizingOptions({ isTabletUp, isMdUp, isXlUp });
            const inlineCardWidth = getInlineCardWidth(
                containerWidth,
                sections.length,
                inlineGap,
                inlineMaxWidth,
            );
            const shouldFitInline = isXlUp
                && sections.length > 0
                && sections.length <= 4
                && inlineCardWidth >= XL_MIN_INLINE_CARD_WIDTH
                && canFitCardsInline(
                    containerWidth,
                    sections.length,
                    inlineGap,
                    inlineMaxWidth,
                );

            if (shouldFitInline && containerWidth > 0) {
                const nextCardWidth = inlineCardWidth;

                setFitsInline((current) => (current === true ? current : true));
                setCardWidth((current) => {
                    if (current === nextCardWidth) {
                        return current;
                    }

                    bodyContentHeightsRef.current = {};
                    contentHeightResolvedRef.current = false;
                    contentHeightRefineRef.current = false;
                    setCardHeightOverride(null);
                    setHasResolvedContentHeight(false);
                    return nextCardWidth;
                });
                return;
            }

            const nextCardWidth = getResponsiveCardWidth(
                containerWidth,
                sections.length,
                false,
                {
                    forceScroll: true,
                    ...scrollOptions,
                },
            );

            setFitsInline((current) => (current === false ? current : false));
            setCardWidth((current) => {
                if (current === nextCardWidth) {
                    return current;
                }

                bodyContentHeightsRef.current = {};
                contentHeightResolvedRef.current = false;
                contentHeightRefineRef.current = false;
                setCardHeightOverride(null);
                setHasResolvedContentHeight(false);
                return nextCardWidth;
            });
        };

        const scheduleMeasure = () => {
            if (measureFrameRef.current !== null) {
                return;
            }

            measureFrameRef.current = requestAnimationFrame(() => {
                measureFrameRef.current = null;
                measure();
            });
        };

        measure();

        const element = wrapperRef.current;
        const resizeObserver = typeof ResizeObserver !== 'undefined' && element
            ? new ResizeObserver(scheduleMeasure)
            : null;

        if (resizeObserver && element) {
            resizeObserver.observe(element);
        }

        window.addEventListener('resize', scheduleMeasure);

        return () => {
            if (measureFrameRef.current !== null) {
                cancelAnimationFrame(measureFrameRef.current);
                measureFrameRef.current = null;
            }

            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleMeasure);
        };
    }, [sections.length, isTabletUp, isMdUp, isXlUp, inlineMaxWidth]);

    useLayoutEffect(() => {
        if (!scrollRef.current || !sections.length || !isScrollMode) {
            return undefined;
        }

        const element = scrollRef.current;

        const alignToGold = () => {
            scrollCarouselToGoldStart(element);
            setActiveSlideIndex(sections.length - 1);
            requestAnimationFrame(syncActiveSlideFromScroll);
        };

        alignToGold();

        const frameId = requestAnimationFrame(() => {
            alignToGold();
            requestAnimationFrame(alignToGold);
        });
        const timeoutId = window.setTimeout(alignToGold, 50);

        return () => {
            cancelAnimationFrame(frameId);
            window.clearTimeout(timeoutId);
        };
    }, [sections.length, cardWidth, isScrollMode, syncActiveSlideFromScroll]);

    useEffect(() => {
        const element = scrollRef.current;

        if (!element || !isScrollMode || sections.length <= 1) {
            return undefined;
        }

        element.addEventListener('scroll', syncActiveSlideFromScroll, { passive: true });
        syncActiveSlideFromScroll();

        return () => {
            element.removeEventListener('scroll', syncActiveSlideFromScroll);
        };
    }, [sections.length, cardWidth, isScrollMode, syncActiveSlideFromScroll]);

    if (!sections.length) {
        return null;
    }

    return (
        <CarouselWrapper
            className="hot-yoter-how-to-get-carousel-wrapper"
            testId={`${testID}-wrapper`}
        >
            <CarouselRoot
                ref={wrapperRef}
                dir="rtl"
                allowVerticalOverflow={cardHeightOverride != null || fitsInline}
                className={`hot-yoter-how-to-get-carousel${fitsInline ? ' hot-yoter-how-to-get-carousel_fits-inline' : ' hot-yoter-how-to-get-carousel_scroll-mode'}${cardHeightOverride != null ? ' hot-yoter-how-to-get-carousel_has-tall-cards' : ''}`}
                testId={testID}
            >
                <CarouselScroll
                    ref={scrollRef}
                    dir={isScrollMode ? 'rtl' : 'ltr'}
                    className="hot-yoter-how-to-get-carousel__scroll"
                    fitsInline={fitsInline}
                    isDesktopInline={isDesktopInline}
                    isScrollMode={isScrollMode}
                    hasHeightOverride={cardHeightOverride != null}
                    cardRowHeight={isTabletUp && !isMobile ? cardRowHeight : null}
                    testId={`${testID}-scroll`}
                >
                    {carouselItems.map(({ section, index, key }) => (
                        <HotYoterHowToGetCard
                            key={key}
                            section={section}
                            index={index}
                            total={sections.length}
                            cardWidth={cardWidth}
                            cardMaxWidth={cardMaxWidth}
                            cardHeightOverride={cardHeightOverride}
                            fitsInline={fitsInline}
                            isMdUp={isMdUp}
                            hasResolvedContentHeight={hasResolvedContentHeight}
                            onBodyContentHeightReport={handleBodyContentHeightReport}
                            testID={testID}
                        />
                    ))}
                </CarouselScroll>

            </CarouselRoot>

                {showScrollDots ? (
                    <CarouselDots
                        className="hot-yoter-how-to-get-carousel__dots"
                        testId={`${testID}-dots`}
                        sx={{ direction: 'ltr' }}
                        aria-hidden={false}
                    >
                        {dotItems.map(({ section, index }) => (
                            <CarouselDot
                                key={`${section.title}-${index}-dot`}
                                type="button"
                                active={activeSlideIndex === index}
                                className={`hot-yoter-how-to-get-carousel__dot${activeSlideIndex === index ? ' hot-yoter-how-to-get-carousel__dot_active' : ''}`}
                                onClick={() => handleDotClick(index)}
                                aria-label={`שלב ${index + 1}`}
                                aria-current={activeSlideIndex === index ? 'true' : undefined}
                                testId={`${testID}-dot-${index}`}
                            />
                        ))}
                    </CarouselDots>
                ) : null}
        </CarouselWrapper>
    );
};
